/**
 * Lightweight in-flight activity counter for dashboard fetch/navigation loaders.
 * Used by the global Progress Ring and any UI that needs "something is loading".
 */

export type LoadingListener = (count: number) => void;

let inflight = 0;
const listeners = new Set<LoadingListener>();

function emit() {
  for (const listener of listeners) {
    listener(inflight);
  }
}

/** Increment the global loading counter; call the returned function when done. */
export function beginLoading(): () => void {
  inflight += 1;
  emit();
  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    inflight = Math.max(0, inflight - 1);
    emit();
  };
}

export function getLoadingCount(): number {
  return inflight;
}

export function isLoadingActive(): boolean {
  return inflight > 0;
}

export function subscribeLoading(listener: LoadingListener): () => void {
  listeners.add(listener);
  listener(inflight);
  return () => {
    listeners.delete(listener);
  };
}

/** Test helper: reset counter between unit tests. */
export function resetLoadingActivityForTests() {
  inflight = 0;
  emit();
}

/**
 * fetch wrapper that drives the global Progress Ring for the duration of the
 * network call. Prefer this for any dashboard path that cannot use api.request().
 */
export async function trackedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const end = beginLoading();
  try {
    return await fetch(input, init);
  } finally {
    end();
  }
}
